import { useContext, useEffect, useMemo, useState } from "react";
import {
  AddWellForm,
  AlertComponent,
  DialogComponent,
  ModalComponent,
  NavBar,
} from "../../components";
import DataContext from "../../context/DataContext";
import { useFetch, usePermissions } from "../../hooks";
import { usePaginator } from "../../hooks/usePaginator";
import { WellProps } from "../../interfaces/interfaces";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DeleteIcon from "@mui/icons-material/Delete";
import { initial_well_state } from "../../utils/data";
import { Box, Button, Container, Fade, IconButton, Paper, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import {
  ListColumn,
  ListPageLayout,
} from "../../components/shared/ListPageLayout";

interface FetchResponse {
  success?: boolean;
  data: WellProps[];
}

export const WellPage = () => {
  const { paginationModel, setPaginationModel } = usePaginator("wells");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const fetchFilters = useMemo(() => ({ search }), [search]);
  const { data, isLoading, error, fetchData, pagination } =
    useFetch<FetchResponse>(
      "well-paginate",
      paginationModel,
      undefined,
      undefined,
      fetchFilters,
    );
  const [isModalOpen, setIsModalOpen] = useState<Boolean>(false);
  const [wellToEdit, setWellToEdit] = useState<WellProps>(initial_well_state);
  const [isCreateWell, setIsCreateWell] = useState<Boolean>(false);
  const { isSuccess, isError, onCreateWell, onEditWell, onDeleteWell } =
    useContext(DataContext);
  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [tableData, setTableData] = useState<WellProps[] | []>([]);
  const { canManageWells, canDeleteWells } = usePermissions();

  useEffect(() => {
    data && data.data && setTableData(data.data);
  }, [data]);

  const columns: ListColumn<WellProps>[] = [
    {
      key: "name",
      header: "Well",
      render: (r) => r.name,
      sortAccessor: (r) => r.name,
      width: "1.4fr",
    },
    {
      key: "client",
      header: "Client",
      render: (r) => r.client?.name || "",
      sortAccessor: (r) => r.client?.name ?? "",
      width: "1.4fr",
    },
    {
      key: "contact",
      header: "Contact",
      render: (r) => r.contact,
      sortAccessor: (r) => r.contact ?? "",
      width: "1.4fr",
    },
    {
      key: "phoneNumber",
      header: "Phone Number",
      render: (r) => r.phoneNumber,
      sortAccessor: (r) => r.phoneNumber ?? "",
      width: "1.2fr",
    },
  ];

  return (
    <>
      <NavBar title="Wells" buttons={[]} />
      <Box
        sx={{
          marginTop: "80px",
          minHeight: "calc(100vh - 80px)",
          background:
            "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Fade in={true} timeout={600}>
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 2, md: 4 },
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(140, 150, 169, 0.1)",
                boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
              }}
            >
              <ListPageLayout<WellProps>
                title="Wells"
                columns={columns}
                rows={tableData}
                getRowId={(r) => r.id!}
                rowsLoading={isLoading}
                isError={Boolean(error)}
                errorMessage="Wells could not be loaded."
                searchValue={search}
                onSearchChange={(value) => setSearch(value.trim())}
                page={paginationModel.page}
                pageSize={paginationModel.pageSize}
                totalCount={Number(pagination?.totalRecords || 0)}
                onPageChange={(page) =>
                  setPaginationModel({ ...paginationModel, page })
                }
                onPageSizeChange={(pageSize) =>
                  setPaginationModel({ page: 0, pageSize })
                }
                toolbarActions={
                  canManageWells ? (
                    <Button
                      startIcon={<AddBoxIcon />}
                      onClick={() => {
                        setIsCreateWell(true);
                        setIsModalOpen(true);
                      }}
                    >
                      New
                    </Button>
                  ) : undefined
                }
                rowActions={(r) => (
                  <>
                    <Tooltip title="Detail">
                      <IconButton
                        onClick={() => navigate(`/well/${r.id}`)}
                        size="small"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() => {
                          setIsCreateWell(false);
                          setWellToEdit(r);
                          setIsModalOpen(true);
                        }}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canDeleteWells && (
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => {
                            setIdToDelete(r.id!.toString());
                            setIsOpenAlert(true);
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
              />
            </Paper>
          </Fade>
        </Container>
      </Box>
      {isModalOpen && (
        <ModalComponent
          modalContent={
            <AddWellForm
              data={wellToEdit || null}
              onEditWell={
                !isCreateWell
                  ? (values: WellProps) => {
                      let newWellValues = {
                        id: values.id,
                        name: values.name,
                        idClient: values.client?.id,
                        contact: values.contact,
                        phoneNumber: values.phoneNumber,
                      };
                      onEditWell(newWellValues);
                      fetchData();
                      setIsModalOpen(false);
                    }
                  : undefined
              }
              onAddWell={
                isCreateWell
                  ? (values: WellProps) => {
                      onCreateWell(values);
                      fetchData();
                      setIsModalOpen(false);
                    }
                  : undefined
              }
              onCancel={() => {
                setWellToEdit(initial_well_state);
                setIsModalOpen(false);
                setIsCreateWell(false);
              }}
            />
          }
        />
      )}
      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={
            <div>
              Are you sure you want to remove this Well and all the content
              related to it?
            </div>
          }
          onAccept={() => {
            onDeleteWell(idToDelete);
            setIdToDelete("");
            setIsOpenAlert(false);
            fetchData();
          }}
          onCancel={() => {
            setIsOpenAlert(false);
            setIdToDelete("");
          }}
        />
      )}
    </>
  );
};
